/**
 * Affiliate Settings Component
 *
 * Displays affiliate program dashboard with stats, referral link, and payout management
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAffiliate } from '@/hooks/useAffiliate';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Icon } from '@/components/ui/Icon';
import { Select } from '@/components/ui/Select';
import { ICONS } from '@/constants/icons';
import { toast } from '@/utils/toast';
import type { PayoutRequest } from '@/types/affiliate';
import { formatApiError } from '@/services/api';
import { formatEsMoney } from '@/utils/earlySupporterFormat';

export const AffiliateSettings = () => {
  const { t, i18n } = useTranslation('settings');
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const money = (amount: number): string => formatEsMoney(amount, 'EUR', locale);
  const { formatDateOnly } = useTimezone();
  const {
    stats,
    isLoading,
    isAffiliate,
    referralLink,
    referralCode,
    availableBalance,
    join,
    isJoining,
    requestPayout,
    isRequestingPayout,
  } = useAffiliate();

  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    if (referralLink) {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success(t('settings_affiliate_copy_toast'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleJoin = () => {
    join(undefined, {
      onSuccess: () => {
        toast.success(t('settings_affiliate_success_join'));
      },
      onError: (error) => {
        toast.error(formatApiError(error).message);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Not an affiliate yet - show join CTA
  if (!isAffiliate) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
        <div className="p-5 sm:p-8 text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon
              name={ICONS.LINK}
              size="lg"
              color="text-primary-600 dark:text-primary-400"
            />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('settings_affiliate_cta_title')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('settings_affiliate_cta_subtitle')}
          </p>
          <ul className="text-left text-sm text-gray-600 dark:text-gray-400 mb-6 space-y-2">
            <li className="flex items-center gap-2">
              <Icon name={ICONS.CHECK} size="sm" color="text-green-500" />
              {t('settings_affiliate_feature_commission')}
            </li>
            <li className="flex items-center gap-2">
              <Icon name={ICONS.CHECK} size="sm" color="text-green-500" />
              {t('settings_affiliate_feature_tracking')}
            </li>
            <li className="flex items-center gap-2">
              <Icon name={ICONS.CHECK} size="sm" color="text-green-500" />
              {t('settings_affiliate_feature_payouts')}
            </li>
          </ul>
          <button
            onClick={handleJoin}
            disabled={isJoining}
            className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-primary-600-contrast rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isJoining ? t('settings_affiliate_joining') : t('settings_affiliate_join_button')}
          </button>
        </div>
      </div>
    );
  }

  // Is an affiliate - show dashboard
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Referral Link Card */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
        <div className="p-5 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('settings_affiliate_referral_title')}
          </h3>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <input
              type="text"
              value={referralLink || ''}
              readOnly
              className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300"
            />
            <button
              onClick={handleCopyLink}
              className="w-full sm:w-auto px-4 py-2 bg-primary-600 hover:bg-primary-700 text-primary-600-contrast rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Icon name={copied ? ICONS.CHECK : ICONS.COPY} size="sm" />
              {copied ? t('settings_affiliate_copy_success') : t('settings_affiliate_copy_button')}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('settings_affiliate_referral_code')} <span className="font-mono font-medium">{referralCode}</span>
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label={t('settings_affiliate_stat_clicks')}
          value={stats?.stats?.totalClicks ?? 0}
          icon={ICONS.PULSE}
        />
        <StatCard
          label={t('settings_affiliate_stat_conversions')}
          value={stats?.stats?.totalConversions ?? 0}
          icon={ICONS.CHECK_CIRCLE}
        />
        <StatCard
          label={t('settings_affiliate_stat_earned')}
          value={money(stats?.stats?.totalCommission ?? 0)}
          icon={ICONS.CHART}
        />
        <StatCard
          label={t('settings_affiliate_stat_available')}
          value={money(availableBalance)}
          icon={ICONS.WALLET}
          highlight={availableBalance >= 50}
        />
      </div>

      {/* Payout Section */}
      {availableBalance >= 50 && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h4 className="font-medium text-green-800 dark:text-green-200">
                {t('settings_affiliate_payout_available_title')}
              </h4>
              <p className="text-sm text-green-600 dark:text-green-400">
                {t('settings_affiliate_payout_available_label')} {money(availableBalance)}
              </p>
            </div>
            <button
              onClick={() => setShowPayoutModal(true)}
              className="w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('settings_affiliate_payout_button')}
            </button>
          </div>
        </div>
      )}

      {availableBalance < 50 && availableBalance > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('settings_affiliate_payout_minimum', { amount: money(50 - availableBalance), min: money(50) })}
          </p>
        </div>
      )}

      {/* Recent Conversions */}
      {stats?.recentConversions && stats.recentConversions.length > 0 && (
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-coral" />
          <div className="p-5 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('settings_affiliate_conversions_title')}
            </h3>
            <div className="space-y-3">
              {stats.recentConversions.slice(0, 5).map((conversion, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {conversion.productName || conversion.orderId}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDateOnly(conversion.convertedAt)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">
                      {t('settings_affiliate_conversion_amount', { amount: money(conversion.commissionAmount) })}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {conversion.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Payout Modal */}
      {showPayoutModal && (
        <PayoutModal
          availableBalance={availableBalance}
          onClose={() => setShowPayoutModal(false)}
          onSubmit={(data) => {
            requestPayout(data, {
              onSuccess: () => {
                toast.success(t('settings_affiliate_success_payout'));
                setShowPayoutModal(false);
              },
              onError: (error) => {
                toast.error(formatApiError(error).message);
              },
            });
          }}
          isLoading={isRequestingPayout}
        />
      )}
    </div>
  );
};

/**
 * Stat Card Component
 */
interface StatCardProps {
  label: string;
  value: string | number;
  icon: string;
  highlight?: boolean;
}

const StatCard = ({ label, value, icon, highlight = false }: StatCardProps) => (
  <div
    className={`rounded-lg border p-4 ${
      highlight
        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon
        name={icon}
        size="sm"
        color={highlight ? 'text-green-600' : 'text-gray-400'}
      />
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    </div>
    <p
      className={`text-xl font-semibold ${
        highlight
          ? 'text-green-600 dark:text-green-400'
          : 'text-gray-900 dark:text-white'
      }`}
    >
      {value}
    </p>
  </div>
);

/**
 * Payout Modal Component
 */
interface PayoutModalProps {
  availableBalance: number;
  onClose: () => void;
  onSubmit: (data: PayoutRequest) => void;
  isLoading: boolean;
}

const PayoutModal = ({ availableBalance, onClose, onSubmit, isLoading }: PayoutModalProps) => {
  const { t, i18n } = useTranslation('settings');
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const [amount, setAmount] = useState(Math.min(availableBalance, 50));
  const [paymentMethod, setPaymentMethod] = useState<PayoutRequest['paymentMethod']>('paypal');
  const [paymentDetails, setPaymentDetails] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ amount, paymentMethod, paymentDetails });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('settings_affiliate_payout_button')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="amount"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              {t('settings_affiliate_modal_amount_label')}
            </label>
            <input
              id="amount"
              type="number"
              min={50}
              max={availableBalance}
              step={0.01}
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('settings_affiliate_available_balance', { amount: formatEsMoney(availableBalance, 'EUR', locale), min: formatEsMoney(50, 'EUR', locale) })}
            </p>
          </div>

          <div>
            <Select
              label={t('settings_affiliate_modal_method_label')}
              value={paymentMethod}
              onValueChange={(value) => setPaymentMethod(value as PayoutRequest['paymentMethod'])}
              options={[
                { value: 'paypal', label: t('settings_affiliate_payout_paypal') },
                { value: 'bank_transfer', label: t('settings_affiliate_payout_bank') },
                { value: 'wise', label: t('settings_affiliate_payout_wise') },
                { value: 'revolut', label: t('settings_affiliate_payout_revolut') }
              ]}
              className="w-full"
            />
          </div>

          <div>
            <label
              htmlFor="payment-details"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              {paymentMethod === 'paypal'
                ? t('settings_affiliate_modal_details_paypal')
                : paymentMethod === 'bank_transfer'
                  ? t('settings_affiliate_modal_details_bank')
                  : paymentMethod === 'wise'
                    ? t('settings_affiliate_modal_details_wise')
                    : t('settings_affiliate_modal_details_revolut')}
            </label>
            <input
              id="payment-details"
              type="text"
              value={paymentDetails}
              onChange={(e) => setPaymentDetails(e.target.value)}
              placeholder={
                paymentMethod === 'paypal'
                  ? 'your@email.com'
                  : paymentMethod === 'bank_transfer'
                    ? 'DE89 3704 0044 0532 0130 00'
                    : paymentMethod === 'wise'
                      ? 'your@email.com'
                      : '@username'
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('settings_modal_cancel')}
            </button>
            <button
              type="submit"
              disabled={
                isLoading || amount < 50 || amount > availableBalance || !paymentDetails
              }
              className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-primary-600-contrast rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isLoading ? t('settings_affiliate_modal_submitting') : t('settings_affiliate_payout_button')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
